/**
 * The record-level actions menu: a ⋮ in the record's HEADER row, left of CLOSE / SAVE /
 * SAVE & CLOSE (`<Header recordActions={{ sections }}>`). See src/blank/PATTERNS.md
 * "Record actions ⋮ in the header row" (BP-09) and BlankEdit.tsx.
 *
 * Aaron 2026-09-27 (g2 aircraft record): the reports control is "in the right place
 * but wrong icon". Its shape takes the reverted Antigravity RecordActionsMenu (1787c89ca)
 * as input: a ⋮ with Print / Export / Display sections. It is re-derived on the house
 * menu vocabulary instead: the SAME `sections` types and dropdown tokens as the create
 * menu (datagrid/CreateMenu.tsx), no colour literals, and no built-in preview dialog
 * (each item owns what it opens).
 *
 * Aaron 2026-09-30 (mustang flight record, H233): "move the print menu up to the header
 * row with the correct colours for a button menu here". It moved from the right of the
 * TabBar to the header row, and its button is `HeaderMenuButton` — the header buttons'
 * own chrome and CLOSE's palette, owned in Buttons.tsx, never restyled by a caller.
 *
 * Honesty rules, enforced here rather than left to callers:
 *   - a section with no visible item is not rendered (no dead heading);
 *   - with nothing to offer at all, the ⋮ itself is not rendered.
 */
import React, { useState } from "react";
import { Box, ListItemText, ListSubheader, Menu, MenuItem, Tooltip } from "@mui/material";

import { HeaderMenuButton } from "./Buttons";

import type { CreateMenuSection } from "./datagrid/CreateMenu";
import {
  dropdownMenuItemSxFor,
  dropdownMenuListItemTextSx,
  dropdownMenuListProps,
  dropdownMenuNestedItemSx,
  dropdownMenuPaperProps,
  dropdownMenuSectionDividerSx,
  dropdownMenuSectionLabelSx,
} from "./dropdownMenuStyles";

export type RecordActionsSection = CreateMenuSection;

/**
 * The ⋮'s tooltip and accessible name, on every record. Aaron 2026-09-30 (H262,
 * TICKET-23044): "Print and Settings" (was "Record actions").
 */
export const RECORD_ACTIONS_LABEL = "Print and Settings";

export interface RecordActionsKebabProps {
  /** Conventionally "Print", "Export", "Display": only sections with real handlers. */
  sections: RecordActionsSection[];
  /** e.g. a new, unsaved record, which has nothing to print yet. */
  disabled?: boolean;
  /** Menu element id, for aria-controls. */
  id?: string;
}

const RecordActionsKebab: React.FC<RecordActionsKebabProps> = ({ sections, disabled = false, id = "record-actions-menu" }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);

  const visibleSections = sections
    .map((s) => ({ ...s, items: s.items.filter((item) => !item.hidden) }))
    .filter((s) => s.items.length > 0);
  if (visibleSections.length === 0) return null;

  return (
    <>
      <Tooltip title={RECORD_ACTIONS_LABEL}>
        <span>
          <HeaderMenuButton
            aria-label={RECORD_ACTIONS_LABEL}
            aria-controls={anchorEl ? id : undefined}
            aria-haspopup="true"
            aria-expanded={anchorEl ? "true" : undefined}
            disabled={disabled}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
        </span>
      </Tooltip>
      <Menu
        id={id}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={dropdownMenuPaperProps}
        MenuListProps={dropdownMenuListProps}
      >
        {visibleSections.map((section, index) => (
          <React.Fragment key={`${section.label}-${index}`}>
            {index > 0 ? <Box component="li" aria-hidden sx={dropdownMenuSectionDividerSx} /> : null}
            <ListSubheader className="list-subheader-item" sx={dropdownMenuSectionLabelSx}>
              {section.label}
            </ListSubheader>
            {section.items.map((item, itemIndex) => {
              const reason = item.disabled ? item.disabledReason : undefined;
              return (
                <MenuItem
                  key={`${item.name}-${itemIndex}`}
                  sx={dropdownMenuItemSxFor(dropdownMenuNestedItemSx, reason)}
                  disabled={item.disabled}
                  onClick={() => {
                    close();
                    item.onClick();
                  }}
                >
                  {/* A disabled entry says WHY, as its secondary line beneath the label —
                      the same rule and rendering as CreateMenu (H104, Pattern 340 §2b); the
                      two menus share the item type, so the reason must not render in one and
                      vanish in the other (H114). */}
                  <ListItemText sx={dropdownMenuListItemTextSx} primary={item.name} secondary={reason} />
                </MenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
};

export default RecordActionsKebab;
